import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import FormContextHeader from '../../components/ui/FormContextHeader';
import Icon from '../../components/AppIcon';
import EmbedMethodTab from './components/EmbedMethodTab';
import EmbedConfigPanel from './components/EmbedConfigPanel';
import DomainSettingsCard from './components/DomainSettingsCard';
import SecuritySettingsCard from './components/SecuritySettingsCard';
import EmbedPreviewCard from './components/EmbedPreviewCard';
import IntegrationTestCard from './components/IntegrationTestCard';
import { whiteLabelService } from '../../services/whiteLabelService';

const EmbedSettings = () => {
  const [activeTab, setActiveTab] = useState('javascript');
  const [embedConfig, setEmbedConfig] = useState({
    autoResize: true,
    cssIsolation: true,
    lazyLoad: false,
    loadingStrategy: 'eager',
    position: 'inline',
    cdnEnabled: true,
    cacheEnabled: true
  });
  const [whiteLabelSettings, setWhiteLabelSettings] = useState(null);

  useEffect(() => {
    whiteLabelService?.getSettings()?.then((data) => setWhiteLabelSettings(data))?.catch(() => {});
  }, []);

  const isWhiteLabel = whiteLabelSettings?.isEnabled && whiteLabelSettings?.customDomain;
  const cdnBase = isWhiteLabel
    ? `https://${whiteLabelSettings?.customDomain}`
    : 'https://cdn.webforms.biz';
  const formBase = isWhiteLabel
    ? `https://${whiteLabelSettings?.customDomain}/f`
    : 'https://forms.webforms.biz';
  const embedJsVar = isWhiteLabel ? 'Forms' : 'WebForms';

  const embedMethods = [
    {
      id: 'javascript',
      label: 'JavaScript',
      icon: 'Code2',
      description: 'Recommended for most websites with dynamic loading',
      features: ['Auto-resize', 'CSS Isolation', 'Event Callbacks'],
      language: 'javascript',
      code: `<script src="${cdnBase}/embed.js"></script>
<script>
  ${embedJsVar}.embed({
    formId: 'wf_abc123xyz',
    container: '#form-container',
    autoResize: true,
    cssIsolation: true,
    onSubmit: (data) => {
      console.log('Form submitted:', data);
    }
  });
</script>
<div id="form-container"></div>`
    },
    {
      id: 'iframe',
      label: 'iFrame',
      icon: 'Frame',
      description: 'Simple embed with complete isolation',
      features: ['Complete Isolation', 'No Dependencies', 'Universal Support'],
      language: 'html',
      code: `<iframe
  src="${formBase}/wf_abc123xyz"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Contact Form">
</iframe>`
    },
    {
      id: 'html',
      label: 'HTML',
      icon: 'FileCode',
      description: 'Direct HTML embed for static sites',
      features: ['No JavaScript', 'SEO Friendly', 'Fast Loading'],
      language: 'html',
      code: `<div data-${isWhiteLabel ? 'forms' : 'webforms'}-embed="wf_abc123xyz"></div>
<script async src="${cdnBase}/embed.js"></script>`
    },
    {
      id: 'react',
      label: 'React',
      icon: 'Component',
      description: 'Native React component for React applications',
      features: ['TypeScript Support', 'React Hooks', 'State Management'],
      language: 'jsx',
      code: isWhiteLabel
        ? `import { FormsEmbed } from '@forms/react';

function App() {
  const handleSubmit = (data) => {
    console.log('Form submitted:', data);
  };

  return (
    <FormsEmbed
      formId="wf_abc123xyz"
      autoResize={true}
      cssIsolation={true}
      onSubmit={handleSubmit}
    />
  );
}`
        : `import { WebFormsEmbed } from '@webforms/react';

function App() {
  const handleSubmit = (data) => {
    console.log('Form submitted:', data);
  };

  return (
    <WebFormsEmbed
      formId="wf_abc123xyz"
      autoResize={true}
      cssIsolation={true}
      onSubmit={handleSubmit}
    />
  );
}`
    },
    {
      id: 'wordpress',
      label: 'WordPress',
      icon: 'Blocks',
      description: 'WordPress plugin with Gutenberg block',
      features: ['Gutenberg Block', 'Shortcode Support', 'Widget Ready'],
      language: 'php',
      code: `// Install the Forms WordPress Plugin
// Then use shortcode:
[forms id="wf_abc123xyz"]

// Or use Gutenberg block:
// Add "Forms Embed" block from editor
// Enter Form ID: wf_abc123xyz`
    }
  ];

  const tabs = [
    { id: 'embed', label: 'Embed Methods', icon: 'Code2' },
    { id: 'domain', label: 'Domain', icon: 'Globe' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'testing', label: 'Testing', icon: 'TestTube2' }
  ];

  const [activeSection, setActiveSection] = useState('embed');

  const handleConfigChange = (newConfig) => {
    setEmbedConfig(newConfig);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'embed':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card border border-border rounded-md overflow-hidden">
                <div className="border-b border-border overflow-x-auto scrollbar-custom">
                  <div className="flex min-w-max">
                    {embedMethods?.map((method) => (
                      <button
                        key={method?.id}
                        onClick={() => setActiveTab(method?.id)}
                        className={`
                          flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium text-sm md:text-base
                          transition-smooth border-b-2 whitespace-nowrap
                          ${activeTab === method?.id
                            ? 'text-primary border-primary bg-primary/5' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon name={method?.icon} size={18} />
                        <span>{method?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  {embedMethods?.filter((method) => method?.id === activeTab)?.map((method) => (
                      <EmbedMethodTab
                        key={method?.id}
                        method={method?.label}
                        code={method?.code}
                        language={method?.language}
                        description={method?.description}
                        features={method?.features}
                      />
                    ))}
                </div>
              </div>

              <EmbedPreviewCard />
            </div>
            <div className="space-y-6">
              <EmbedConfigPanel
                config={embedConfig}
                onConfigChange={handleConfigChange}
              />
            </div>
          </div>
        );

      case 'domain':
        return (
          <div className="max-w-3xl">
            <DomainSettingsCard
              currentDomain="my-forms.webforms.biz"
              onDomainUpdate={(domain) => console.log('Domain updated:', domain)}
            />
          </div>
        );

      case 'security':
        return (
          <div className="max-w-3xl">
            <SecuritySettingsCard />
          </div>
        );

      case 'testing':
        return (
          <div className="max-w-4xl">
            <IntegrationTestCard />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{}}>
        <FormContextHeader
          formTitle="Contact Form"
          lastSaved="2 minutes ago"
          isPublished={true}
          onPreview={() => console.log('Preview')}
          onEmbed={() => console.log('Embed')}
          onPublish={() => console.log('Publish')}
          onSave={() => console.log('Save')}
        />
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                Embed Settings
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                Deploy your form across websites and platforms with customized integration methods
              </p>
            </div>

            <div className="bg-card border border-border rounded-md overflow-hidden">
              <div className="border-b border-border overflow-x-auto scrollbar-custom">
                <div className="flex min-w-max">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveSection(tab?.id)}
                      className={`
                        flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium text-sm md:text-base
                        transition-smooth border-b-2 whitespace-nowrap
                        ${activeSection === tab?.id
                          ? 'text-primary border-primary bg-primary/5' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 md:p-6 lg:p-8">
                {renderContent()}
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-md p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/20 rounded-md flex items-center justify-center">
                    <Icon name="Lightbulb" size={24} color="var(--color-primary)" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-heading font-semibold text-primary mb-2">
                    Deployment Best Practices
                  </h3>
                  <ul className="space-y-2 text-sm text-primary/90">
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle2" size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Test your embed on staging environment before production deployment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle2" size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Enable CSS isolation to prevent style conflicts with your website</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle2" size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Use lazy loading for forms below the fold to improve page speed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle2" size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Configure domain restrictions to prevent unauthorized embedding</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </NavigationBar>
    </div>
  );
};

export default EmbedSettings;